import ActionConfirmationDialog from '../../../components/ActionConfirmationDialog';
import axios from 'axios';
import dayjs from 'dayjs';
import env from '../../../config';
import StarterKit from '@tiptap/starter-kit';
import { ActionBar, Button, Checkbox, Dialog, Portal, Stack, Text } from '@chakra-ui/react';
import { Control, RichTextEditor } from '../../../components/ui/rich-text-editor';
import { FiSlash, FiTrash2 } from 'react-icons/fi';
import { lessonDelete } from '../../../operations/admin/lessonDelete';
import { LessonStatus } from '@opanuj/shared/types';
import { lessonStatusUpdate } from '../../../operations/admin/lessonStatusUpdate';
import { LessonWithClient } from './LessonsController';
import { renderEmail } from '../../../utils/renderEmail';
import { useDatabase } from '../../../hooks/useDatabase';
import { useEditor } from '@tiptap/react';
import { useEffect, useMemo, useState } from 'react';
import { useEmailTemplatesList } from '../../../hooks/useEmailTemplates';

type PendingAction = 'delete' | 'cancel' | null;

function LessonSelectionActionBar({
	lesson,
	onClearSelection,
}: {
	lesson: LessonWithClient | null;
	onClearSelection: () => void;
}) {
	const database = useDatabase();
	const [pendingAction, setPendingAction] = useState<PendingAction>(null);
	const [isPending, setIsPending] = useState(false);
	const [sendClientEmail, setSendClientEmail] = useState(true);
	const [isCancellationDialogOpen, setCancellationDialogOpen] = useState(false);
	const [templates] = useEmailTemplatesList();
	const cancellationTemplate = useMemo(
		() => templates.find((template) => template.id === 'lesson-cancelled-client') ?? null,
		[templates],
	);

	const description = lesson ? [
		dayjs(lesson.timestamp * 1000).locale('pl').format('dd DD.MM H:mm'),
		lesson.teacher,
		lesson.code,
		lesson.students.map((student) => student.name).join(', '),
	].filter(Boolean).join(' • ') : '';
	const clientEmails = [...new Set(
		lesson?.students.flatMap((student) => (student.client?.notifications?.emails ?? []),
		) ?? [],
	)];
	const renderedCancellationTemplate = useMemo(() => renderEmail(
		cancellationTemplate?.html ?? '<p>Treść wiadomości...</p>',
		{ lesson: description },
	), [cancellationTemplate?.html, description]);
	const editor = useEditor({
		extensions: [StarterKit],
		content: renderedCancellationTemplate,
		immediatelyRender: false,
		editorProps: {
			attributes: {
				style: 'min-height: 180px; padding: 0.75rem 1rem; outline: none;',
			},
		},
	});

	useEffect(() => {
		if (editor && editor.getHTML() !== renderedCancellationTemplate) {
			editor.commands.setContent(renderedCancellationTemplate, { emitUpdate: false });
		}
	}, [editor, renderedCancellationTemplate]);

	if (!lesson) return null;

	const handleConfirm = async (action: PendingAction = pendingAction) => {
		setIsPending(true);
		try {
			if (action === 'delete') {
				await lessonDelete(database, { id: lesson.id });
				onClearSelection();
			} else if (action === 'cancel') {
				await lessonStatusUpdate(database, { id: lesson.id, status: LessonStatus.Cancelled });
				if (sendClientEmail && clientEmails.length && cancellationTemplate) {
					await axios.post(env.endpoints.sendEmail, {
						to: clientEmails,
						subject: cancellationTemplate.subject || 'Informacja o odwołaniu zajęć w Opanuj.to',
						html: editor?.getHTML() || renderedCancellationTemplate,
						bcc: ['sekretariat@opanuj.to', 'rsoszynski121@gmail.com'],
					}, { headers: { 'Content-Type': 'application/json' } });
				}
			}
		} finally {
			setIsPending(false);
			setPendingAction(null);
			setCancellationDialogOpen(false);
		}
	};

	return (
		<>
			<ActionBar.Root open>
				<ActionBar.Positioner>
					<ActionBar.Content>
						<ActionBar.SelectionTrigger>
							<Text fontSize="sm" fontWeight="semibold">{description}</Text>
						</ActionBar.SelectionTrigger>
						<ActionBar.Separator />
						<Button size="sm" colorPalette="red" variant="outline" onClick={() => setPendingAction('delete')}>
							<FiTrash2 />
							Usuń
						</Button>
						<Button
							size="sm"
							colorPalette="orange"
							variant="surface"
							disabled={lesson.status === LessonStatus.Cancelled}
							onClick={() => setCancellationDialogOpen(true)}
						>
							<FiSlash />
							Odwołaj
						</Button>
						<ActionBar.CloseTrigger asChild>
							<Button size="sm" variant="ghost" onClick={onClearSelection}>
								Odznacz
							</Button>
						</ActionBar.CloseTrigger>
					</ActionBar.Content>
				</ActionBar.Positioner>
			</ActionBar.Root>

			<ActionConfirmationDialog
				isOpen={pendingAction === 'delete'}
				isPending={isPending}
				onClose={() => setPendingAction(null)}
				onAction={handleConfirm}
				title={pendingAction === 'delete' ? 'Usuń lekcję' : 'Odwołaj lekcję'}
				bodyText={pendingAction === 'delete'
					? 'Czy na pewno chcesz usunąć tę lekcję? Ta akcja nie wysyła powiadomień do klientów ani nauczyciela.'
					: 'Czy na pewno chcesz odwołać tę lekcję?'}
				actionText={pendingAction === 'delete' ? 'Usuń' : 'Odwołaj'}
				actionColor={pendingAction === 'delete' ? 'red' : 'orange'}
			/>

			<Dialog.Root open={isCancellationDialogOpen} onOpenChange={(event) => !event.open && setCancellationDialogOpen(false)} role="dialog">
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content maxW="900px" w="95vw">
							<Dialog.Header>
								<Dialog.Title>Odwołaj lekcję: {description}</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<Stack gap={4}>
									<Text>Czy na pewno chcesz odwołać tę lekcję?</Text>
									<Checkbox.Root checked={sendClientEmail} onCheckedChange={(details) => setSendClientEmail(Boolean(details.checked))}>
										<Checkbox.HiddenInput />
										<Checkbox.Control />
										<Checkbox.Label>Wyślij email do klienta</Checkbox.Label>
									</Checkbox.Root>
									{sendClientEmail && (
										<Stack gap={2}>
											<Text fontSize="sm" fontWeight="semibold">Treść maila</Text>
											<RichTextEditor.Root editor={editor}>
												<RichTextEditor.Toolbar>
													<RichTextEditor.ControlGroup>
														<Control.Bold />
														<Control.Italic />
														<Control.Underline />
														<Control.Link />
													</RichTextEditor.ControlGroup>
													</RichTextEditor.Toolbar>
												<RichTextEditor.Content />
											</RichTextEditor.Root>
										</Stack>
									)}
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button variant="ghost" onClick={() => setCancellationDialogOpen(false)} disabled={isPending}>Anuluj</Button>
								<Button colorPalette="orange" onClick={() => void handleConfirm('cancel')} loading={isPending}>Odwołaj</Button>
							</Dialog.Footer>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
		</>
	);
}

export default LessonSelectionActionBar;
